// ============================================================
// pageContext — エージェントに「いま画面で何を見ているか」を伝える
//
// 正: booking-platform/md/ai-control-center.md §15（全操作エージェント）
//
// 各ページが自分の文脈（ページ名・選択中の予約/顧客/メール）をここに公開し、
// useAgent がシステムプロンプトに注入する。
// 「この予約をキャンセルして」の「この」を **決定的に** 解決するための仕組みで、
// AI に画面の状態を推測させない（agent-rules #1 捏造禁止の構造的担保。reconcile 方式と同じ）。
//
// 公開は各ページの責務: 選択が変わったら setPageContext、ページを離れたら clearPageContext。
// ============================================================

export type PageId =
    | 'calendar' | 'reservations' | 'day_list' | 'line' | 'mail-queue'
    | 'customers' | 'stock' | 'reconcile' | 'dashboard' | 'other';

export interface PageSelection {
    type: 'reservation' | 'customer' | 'mail' | 'vehicle';
    /** 予約なら contact_code、顧客なら顧客ID、メールなら mail_id */
    id: string;
    /** プロンプトに載せる短い説明（例: "予約 R071B8EB 大北 啓介 様 9/13 貸出"） */
    label: string;
}

export interface PageContext {
    page: PageId;
    /** ユーザーがいま開いている・選択している対象（無ければ undefined） */
    selection?: PageSelection;
}

let current: PageContext = { page: 'other' };

export function setPageContext(ctx: PageContext): void {
    current = ctx;
}

/** 選択だけ更新する（ページは維持） */
export function setPageSelection(selection: PageSelection | undefined): void {
    current = { ...current, selection };
}

export function clearPageContext(page?: PageId): void {
    // 他ページが既に上書きしている場合は消さない（アンマウント順の競合対策）
    if (page && current.page !== page) return;
    current = { page: 'other' };
}

export function getPageContext(): PageContext {
    return current;
}

const PAGE_LABEL: Record<PageId, string> = {
    calendar: '予約カレンダー',
    reservations: '予約一覧',
    day_list: '貸出返却リスト',
    line: 'LINEチャット',
    'mail-queue': 'メールキュー',
    customers: '顧客データベース',
    stock: '在庫管理',
    reconcile: 'データ整合',
    dashboard: 'ダッシュボード',
    other: '(不明)',
};

/** システムプロンプトに注入する文脈ブロック。文脈が無ければ空文字。 */
export function buildPageContextPrompt(): string {
    const c = current;
    const lines: string[] = [`# 現在の画面\n- ユーザーは「${PAGE_LABEL[c.page]}」ページを開いている。`];
    if (c.selection) {
        lines.push(`- 選択中: ${c.selection.label}`);
        lines.push('- ユーザーが「この予約」「これ」と言ったら、上記の選択中の対象を指す。推測せずこれを使う。');
    } else {
        lines.push('- 何も選択されていない。「この予約」等と言われたら、どれを指すかを必ず聞き返す。');
    }
    return lines.join('\n');
}
